/* Wizard · Shared primitives (layouts, chips, review-helpers, indicators)
 * Extracted iter101.91 from `/app/frontend/src/pages/doctor/CaseSubmissionPage.jsx`.
 *
 * Everything in this file is a *display* primitive shared across
 * multiple wizard steps or their sub-blocks. Keeping them together
 * makes the visual grammar (spacing, chip tones, section shells)
 * easy to audit in one place.
 *
 * Contents (dependency-ordered):
 *   Small primitives  → SelectedPill · ReviewBlock · Row · RecapChip
 *   Section chrome    → Step1SectionHeader · Step1ProgressSpine
 *                       · Step1AnsweredStack · Step1AnsweredStrip
 *                       · Step1ActiveWrap · Step1RecapBanner
 *   Wizard chrome     → StepIndicator · WizardStepHero
 *   Draft affordances → DraftSavedChip · ChildCaseBanner
 *   Form helper       → SelectField
 */
import React, { useState, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { API } from "@/lib/api";
import {
    CaretRight, CheckCircle, Circle, Check, Warning, Info, Sparkle,
    Star, ShieldCheck, Crown, Lightning, Prescription, Tooth, Headset,
    Cube, Truck, Receipt, SealCheck, Hourglass, GitBranch, Package,
    ArrowRight, ClipboardText, Camera, Buildings, Hash, Calculator,
    HouseLine, Stethoscope, Scan, Ruler, Lock, Sliders, ChartBar,
    Clock, Cloud, CloudArrowUp, HeartStraight, Pencil,
    FileArrowUp, ArrowsCounterClockwise,
    User as UserIcon,
} from "@phosphor-icons/react";




/* ═══════════════ layout ═══════════════ */
export function DraftSavedChip({ lastSavedAt }) {
    const [tick, setTick] = React.useState(0);
    React.useEffect(() => {
        if (!lastSavedAt) return;
        const t = setInterval(() => setTick((n) => n + 1), 30 * 1000);
        return () => clearInterval(t);
    }, [lastSavedAt]);
    if (!lastSavedAt) return null;
    // Rebuild the relative label using `tick` as a dep just so React
    // re-renders every 30s.
    void tick;
    const secs = Math.max(0, Math.floor((Date.now() - lastSavedAt.getTime()) / 1000));
    let label;
    if (secs < 10) label = "just now";
    else if (secs < 60) label = `${secs}s ago`;
    else if (secs < 3600) label = `${Math.floor(secs / 60)}m ago`;
    else label = `${Math.floor(secs / 3600)}h ago`;
    return (
        <span
            className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-emerald-50 border border-emerald-200/70 text-emerald-800 text-[10.5px] font-semibold shadow-[0_1px_0_rgba(0,0,0,0.02)]"
            data-testid="wizard-draft-saved-chip"
            title={`Draft last saved: ${lastSavedAt.toLocaleTimeString()}`}
        >
            <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
            </span>
            Draft saved · <span className="font-normal opacity-80">{label}</span>
        </span>
    );
}



export function ChildCaseBanner({ caseKind, parentCaseId, parentCaseCode }) {
    const isRefinement = caseKind === "refinement";
    const Icon = isRefinement ? ArrowsCounterClockwise : Crown;
    const tone = isRefinement
        ? { bg: "bg-amber-50",  border: "border-amber-300", text: "text-amber-900", chip: "bg-amber-100 text-amber-900", iconBg: "bg-amber-100", iconColor: "text-amber-700" }
        : { bg: "bg-violet-50", border: "border-violet-300", text: "text-violet-900", chip: "bg-violet-100 text-violet-900", iconBg: "bg-violet-100", iconColor: "text-violet-700" };
    const token = typeof window !== "undefined" ? localStorage.getItem("ca_token") || "" : "";
    return (
        <div
            className={`mt-3 rounded-lg border ${tone.border} ${tone.bg} px-3 py-2.5 flex items-center gap-3 flex-wrap`}
            data-testid="case-wizard-child-banner"
        >
            <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${tone.iconBg}`}>
                <Icon size={16} weight="fill" className={tone.iconColor} />
            </div>
            <div className="flex-1 min-w-0">
                <div className={`text-xs font-bold uppercase tracking-wider ${tone.text}`}>
                    {isRefinement ? "Refinement" : "Retainer order"} · linked to {parentCaseCode || "parent case"}
                </div>
                <div className="text-[11px] text-muted-foreground">
                    Patient + provider are inherited from the parent and can't be changed here. Previous records are pre-attached as reference — replace any slot that's no longer accurate.
                </div>
            </div>
            <a
                href={`${API}/cases/${parentCaseId}/pdf/submission?auth=${token}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${tone.chip} hover:opacity-90`}
                data-testid="case-wizard-parent-pdf-link"
            >
                <FileArrowUp size={11} weight="bold" /> View original submission
            </a>
        </div>
    );
}


/* Step-level accent palette — iter98.99l
 *
 * Each wizard step carries a distinct accent so doctors get a
 * memorable color cue per step. The same palette feeds both the
 * `StepIndicator` (top of the wizard) and the `WizardStepHero`
 * (above each step body) — every step is "owned" by one color.
 *
 * Color choices map to the step's personality:
 *   1 primary  — Case Setup    (brand teal, starting point)
 *   2 violet   — Clinical Exam (premium/clinical decision)
 *   3 amber    — Scans & Photos (records capture, warm)
 *   4 emerald  — Treatment Plan (growth/movement)
 *   5 rose     — Lab Instructions (handoff, urgent)
 *   6 primary  — Review & Submit (back to brand, completion)
 *   7 primary  — Self-Plan Review (same)
 *
 * Self-Plan's Step 6 (Choose Tier) gets its own override: keeps the
 * primary accent (Step 6 = decision step in both modes) but its hero
 * uses Crown icon + tier-specific copy.
 */
const STEP_PALETTE = {
    // iter100.111 — Meaningful, outcome-focused subtitles across all
    // steps. Each line answers "why this step matters", not just what
    // fields to fill. Rendered uniformly in rose + medium weight so the
    // doctor's eye lands on the "why" before diving into the form.
    1: { accent: "primary", icon: Sparkle,       title: "Case Setup",       sub: "Five quick decisions that shape this case's workflow, timeline and price" },
    2: { accent: "violet",  icon: Stethoscope,   title: "Clinical Exam",    sub: "The diagnostic snapshot your treatment plan will be built on" },
    3: { accent: "amber",   icon: Camera,        title: "Scans & Photos",   sub: "The records our lab uses to model your patient in 3D" },
    4: { accent: "emerald", icon: Tooth,         title: "Treatment Plan",   sub: "Turn your clinical judgment into a manufacturable prescription" },
    5: { accent: "rose",    icon: ClipboardText, title: "Lab Instructions", sub: "Wear rules and manufacturing guidance that fit your patient's life" },
    6: { accent: "primary", icon: CheckCircle,   title: "Review & Submit",  sub: "One last look before the lab starts on your case" },
    7: { accent: "primary", icon: CheckCircle,   title: "Review & Submit",  sub: "One last look before the lab starts on your case" },
};
const STEP_ACCENT_CLASSES = {
    primary: {
        icon:       "text-primary",
        bgSoft:     "bg-primary/12",
        ring:       "ring-primary/25",
        line:       "bg-primary",
        text:       "text-primary",
        chip:       "bg-gradient-to-br from-primary/15 to-primary/5 text-primary ring-1 ring-primary/25",
        dot:        "fill-primary",
    },
    violet: {
        icon:       "text-violet-600",
        bgSoft:     "bg-violet-100",
        ring:       "ring-violet-200",
        line:       "bg-violet-400",
        text:       "text-violet-700",
        chip:       "bg-gradient-to-br from-violet-100 to-violet-50 text-violet-700 ring-1 ring-violet-200",
        dot:        "fill-violet-500",
    },
    amber: {
        icon:       "text-amber-700",
        bgSoft:     "bg-amber-100",
        ring:       "ring-amber-200",
        line:       "bg-amber-400",
        text:       "text-amber-800",
        chip:       "bg-gradient-to-br from-amber-100 to-amber-50 text-amber-800 ring-1 ring-amber-200",
        dot:        "fill-amber-500",
    },
    emerald: {
        icon:       "text-emerald-600",
        bgSoft:     "bg-emerald-100",
        ring:       "ring-emerald-200",
        line:       "bg-emerald-400",
        text:       "text-emerald-700",
        chip:       "bg-gradient-to-br from-emerald-100 to-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
        dot:        "fill-emerald-500",
    },
    rose: {
        icon:       "text-rose-600",
        bgSoft:     "bg-rose-100",
        ring:       "ring-rose-200",
        line:       "bg-rose-400",
        text:       "text-rose-700",
        chip:       "bg-gradient-to-br from-rose-100 to-rose-50 text-rose-700 ring-1 ring-rose-200",
        dot:        "fill-rose-500",
    },
};
function getStepPalette(stepId, planningMode) {
    /* iter98.99o — Self-Plan's old Step 6 override ("Choose Tier" with
       Crown icon) was removed. Self-Plan now matches CoE's 6-step
       structure: Step 6 is Review & Submit in BOTH modes. Tier for
       Self-Plan is auto-assigned from aligner count and surfaced in
       Review — no separate step needed. */
    return STEP_PALETTE[stepId] || STEP_PALETTE[1];
}


export function StepIndicator({ step, steps, planningMode = "coe" }) {
    return (
        <div className="flex items-center justify-between px-1">
            {steps.map((s, i) => {
                const done = s.id < step;
                const active = s.id === step;
                const palette = getStepPalette(s.id, planningMode);
                const accent = STEP_ACCENT_CLASSES[palette.accent] || STEP_ACCENT_CLASSES.primary;
                return (
                    <React.Fragment key={s.id}>
                        <div className={`flex items-center gap-1.5 text-xs ${active ? `font-semibold ${accent.text}` : done ? accent.text : "text-muted-foreground"}`}>
                            {done ? (
                                <CheckCircle size={16} weight="fill" className={accent.icon} />
                            ) : (
                                <Circle size={16} weight={active ? "fill" : "regular"} className={active ? accent.icon : ""} />
                            )}
                            <span className="hidden sm:inline">{s.label}</span>
                            <span className="sm:hidden">{s.id}</span>
                        </div>
                        {i < steps.length - 1 && (
                            <div className={`flex-1 h-px mx-2 transition-colors ${done ? accent.line : "bg-border"}`} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}


/* WizardStepHero — iter98.99l → iter98.99aj
 *
 * Compact step-intro strip (no longer a full card). The hero is now
 * a thin solid-color band — the entire bar is tinted with the step's
 * accent so doctors get a strong color rhythm as they navigate the
 * wizard, but vertical footprint is small and never reads as a
 * separate "section". Title + sub render on the SAME line: the title
 * stays bold/display-weight, the sub follows in smaller italic muted
 * text after a thin divider. Falls back to stacking on very narrow
 * viewports where the row can't fit.
 */
export function WizardStepHero({ step, planningMode, subOverride = null, subTone = null }) {
    const palette = getStepPalette(step, planningMode);
    const Icon = palette.icon;
    const accent = STEP_ACCENT_CLASSES[palette.accent] || STEP_ACCENT_CLASSES.primary;
    // iter101.68 — Optional dynamic subtitle. Step 1 uses this to
    // tell the doctor which decision to pick next as they progress
    // through the four sub-cards ("Next up · pick your scan source"
    // → "…planning mode" → "…compliance" → "…manufacturing speed" →
    // "All picks made — hit Next to continue"). subTone lets the
    // caller flip the subtitle color between the neutral rose (why
    // this step matters) and the accent primary (active guidance).
    const subText = subOverride || palette.sub;
    const subClass =
        subTone === "primary"
            ? "text-[12px] font-semibold text-primary leading-tight truncate not-italic"
            : subTone === "muted"
                ? "text-[12px] font-medium text-muted-foreground leading-tight truncate not-italic"
                : "text-[12px] font-medium text-rose-700 leading-tight truncate not-italic";
    return (
        <div
            className={`rounded-xl border border-border/60 ${accent.bgSoft} px-4 py-2 shadow-[0_1px_0_rgba(0,0,0,0.02)]`}
            data-testid={`case-wizard-step${step}-hero`}
        >
            <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap">
                <div className={`shrink-0 w-7 h-7 rounded-lg grid place-items-center ${accent.icon} bg-white/55 ring-1 ${accent.ring}`}>
                    <Icon size={14} weight="duotone" />
                </div>
                <h2 className={`font-display text-[14.5px] font-semibold leading-none ${accent.text}`}>
                    {palette.title}
                </h2>
                <span className={`hidden sm:inline-block w-px h-3.5 ${accent.line} opacity-50`} aria-hidden="true" />
                {/* iter100.111 — Uniform rose subtitle across every step.
                    Consistent tone + non-italic medium weight so the "why
                    this step matters" line actually gets read instead of
                    treated as decorative caption.
                    iter101.68 — Overridable when the parent has a more
                    actionable next-up hint (e.g. Step 1 progressive
                    disclosure). */}
                <p
                    key={subText}
                    className={`${subClass} animate-in fade-in slide-in-from-left-1 duration-200 ease-out`}
                    data-testid={`case-wizard-step${step}-hero-sub`}
                >
                    {subText}
                </p>
            </div>
        </div>
    );
}


/* Step1SectionHeader — iter98.99i
 *
 * Numbered + iconified section header used inside `StepCaseSetup`.
 * Each Step 1 decision (Scan source · Submission mode · Manufacturing
 * speed) is wrapped with one of these so doctors get a strong visual
 * rhythm — they instantly know there are three sequential choices on
 * this page, and each one carries its own anchor icon for memory.
 *
 * iter98.99k — Extended to per-section accent colors. Each numbered
 * chip + section icon now carry a distinct accent (primary / violet /
 * amber) so the page doesn't read as one wash of teal. The section
 * cards in `StepCaseSetup` provide the boundary; this header provides
 * the identity.
 */
export function Step1SectionHeader({ num, icon: IconComp, title, helper, accent = "primary" }) {
    const palette = {
        primary: {
            chip: "bg-gradient-to-br from-primary/15 to-primary/5 text-primary ring-1 ring-primary/25",
            icon: "text-primary/75",
        },
        violet: {
            chip: "bg-gradient-to-br from-violet-100 to-violet-50 text-violet-700 ring-1 ring-violet-200",
            icon: "text-violet-600/80",
        },
        amber: {
            chip: "bg-gradient-to-br from-amber-100 to-amber-50 text-amber-800 ring-1 ring-amber-200",
            icon: "text-amber-700/85",
        },
        rose: {
            chip: "bg-gradient-to-br from-rose-100 to-rose-50 text-rose-700 ring-1 ring-rose-200",
            icon: "text-rose-600/80",
        },
        sky: {
            chip: "bg-gradient-to-br from-sky-100 to-cyan-50 text-sky-700 ring-1 ring-sky-200",
            icon: "text-sky-600/85",
        },
        steel: {
            chip: "bg-gradient-to-br from-slate-100 to-blue-50 text-slate-700 ring-1 ring-slate-300",
            icon: "text-slate-600/85",
        },
        blue: {
            chip: "bg-gradient-to-br from-blue-100 to-blue-50 text-blue-700 ring-1 ring-blue-200",
            icon: "text-blue-600/85",
        },
        teal: {
            chip: "bg-gradient-to-br from-teal-100 to-teal-50 text-teal-700 ring-1 ring-teal-200",
            icon: "text-teal-600/85",
        },
        emerald: {
            chip: "bg-gradient-to-br from-emerald-100 to-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
            icon: "text-emerald-600/85",
        },
    };
    const p = palette[accent] || palette.primary;
    return (
        <div className="flex items-start gap-3 mb-5" data-testid={`step1-section-${num}`}>
            <div
                className={`shrink-0 w-9 h-9 rounded-xl grid place-items-center font-display font-bold text-[11px] tabular-nums ${p.chip}`}
                aria-hidden="true"
            >
                {num}
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[12px] font-bold tracking-[0.16em] uppercase text-foreground">
                        {title}
                    </span>
                    {IconComp && <IconComp size={13} weight="duotone" className={p.icon} />}
                </div>
                {helper && (
                    <p className="text-[12px] text-muted-foreground mt-1 leading-snug">
                        {helper}
                    </p>
                )}
            </div>
        </div>
    );
}


/*  iter100.113s — Step 1 progress spine.
    Thin chip strip that lights up as the doctor answers each Step-1
    question. Filled dot + colored label for answered items; ghost dot
    for pending. Non-clickable — this is a status indicator, not a
    navigator. Rendered above the first section so it stays visible
    as sections reveal.  */
export function Step1ProgressSpine({ steps }) {
    const doneCount = steps.filter((s) => s.done).length;
    const pct = Math.round((doneCount / steps.length) * 100);
    return (
        <div
            className="flex items-center gap-3 px-4 py-2.5 rounded-full bg-card border border-border shadow-sm overflow-x-auto"
            role="progressbar"
            aria-valuenow={doneCount}
            aria-valuemin={0}
            aria-valuemax={steps.length}
            data-testid="step1-progress-spine"
        >
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-primary shrink-0" data-testid="step1-progress-spine-label">
                Step 1 Progress
            </div>
            <div className="h-4 w-px bg-border shrink-0" aria-hidden="true" />
            <div className="flex items-center gap-2.5 flex-1 min-w-max">
                {steps.map((s, i) => {
                    const isLast = i === steps.length - 1;
                    return (
                        <React.Fragment key={s.key}>
                            <div
                                className={`inline-flex items-center gap-1.5 text-[11px] font-semibold transition-colors whitespace-nowrap ${s.done ? "text-primary" : "text-muted-foreground/70"}`}
                                data-testid={`step1-spine-chip-${s.key}`}
                                aria-current={s.done ? "step" : undefined}
                            >
                                {s.done ? (
                                    <span
                                        className="w-3.5 h-3.5 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-sm ring-2 ring-primary/20"
                                        data-testid={`step1-spine-dot-${s.key}-done`}
                                    >
                                        <Check size={8} weight="bold" />
                                    </span>
                                ) : (
                                    <span
                                        className="w-3.5 h-3.5 rounded-full border-[1.5px] border-muted-foreground/40 bg-background"
                                        data-testid={`step1-spine-dot-${s.key}-pending`}
                                        aria-hidden="true"
                                    />
                                )}
                                <span>{s.label}</span>
                            </div>
                            {!isLast && (
                                <span
                                    className={`h-px w-4 sm:w-6 shrink-0 transition-colors ${s.done ? "bg-primary/40" : "bg-border"}`}
                                    aria-hidden="true"
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
            <div className="tabular-nums text-[10.5px] font-bold text-primary shrink-0 pl-1" data-testid="step1-spine-percent">
                {pct}%
            </div>
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════════
   iter101.63 — Progressive-disclosure primitives for Step 1
   ═══════════════════════════════════════════════════════════════════
   Before this iter, the four Step-1 decisions (Scan Source, Planning
   Mode, Compliance, Manufacturing speed) all stacked as full cards.
   Once you'd picked something you were still staring at that fully-
   expanded card, plus the next one — a lot of vertical noise for
   what should feel like a quick 4-question checkout.

   New pattern: only the active (next-unanswered) decision renders as
   a big card with a teal "Now:" prompt. Everything already answered
   collapses UP into a single-line horizontal chip strip that sits at
   the top of the step card, keeps every past answer glanceable, and
   exposes a "Change" affordance so the doctor can revise without
   navigating.
*/

/* Step1AnsweredStack — Option (a) recap layout, iter101.73 rewrite:
   Each answered decision now renders as a plain-language confirmation
   sentence — "Scan Source — Upload my own scans (you have chosen to
   upload scans in-house)" — so the recap reads like a summary of
   choices instead of a row of status tags. Bigger label, sentence-
   case value, muted helper line, subtle animation on mount. */
export function Step1AnsweredStack({ chips = [] }) {
    if (!chips.length) return null;
    const toneClasses = {
        primary:  { pill: "bg-primary/10 text-primary ring-primary/30",             value: "text-primary",       glow: "shadow-[0_0_0_4px_hsl(var(--primary)/0.15)]" },
        emerald:  { pill: "bg-emerald-100 text-emerald-700 ring-emerald-300/50",    value: "text-emerald-700",   glow: "shadow-[0_0_0_4px_rgb(16_185_129_/_0.15)]" },
        rose:     { pill: "bg-rose-100 text-rose-700 ring-rose-300/50",             value: "text-rose-700",      glow: "shadow-[0_0_0_4px_rgb(244_63_94_/_0.15)]" },
        amber:    { pill: "bg-amber-100 text-amber-800 ring-amber-300/50",          value: "text-amber-800",     glow: "shadow-[0_0_0_4px_rgb(245_158_11_/_0.15)]" },
    };
    return (
        <div className="space-y-2" data-testid="step1-answered-stack">
            {chips.map((c) => {
                const t = toneClasses[c.tone] || toneClasses.primary;
                return (
                    <div
                        key={c.key}
                        className={`group relative rounded-2xl border border-border/80 bg-card px-4 py-3 flex items-center gap-4 shadow-[0_1px_0_rgba(15,23,42,0.02)] hover:border-border transition-colors animate-step1-lock-settle`}
                        data-testid={`step1-answered-chip-${c.key}`}
                    >
                        {/* Numbered pill in the section's accent tone.
                            iter98.99ah — Tiny locked-padlock badge
                            sits on the pill's corner so the doctor sees
                            at a glance that this answer is committed. */}
                        <span
                            className={`relative shrink-0 w-9 h-9 rounded-xl grid place-items-center text-[13px] font-display font-bold tabular-nums ring-1 ${t.pill}`}
                            aria-hidden
                        >
                            {c.num}
                            <span
                                className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-white ring-1 ring-border grid place-items-center ${t.value}`}
                                aria-hidden
                            >
                                <Lock size={9} weight="fill" />
                            </span>
                        </span>

                        {/* Middle column — confirmation sentence + helper. */}
                        <div className="flex-1 min-w-0">
                            {/* Row 1 — "[Label] — [Value]" reads as one
                                sentence: label in muted foreground, em-dash,
                                value in the accent tone (bold). */}
                            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 min-w-0">
                                <span className="text-[13.5px] font-semibold text-foreground/85 shrink-0">
                                    {c.label}
                                </span>
                                <span className="text-muted-foreground/60 shrink-0" aria-hidden>—</span>
                                <span
                                    className={`text-[13.5px] font-bold truncate ${t.value}`}
                                    title={c.value}
                                >
                                    {c.value}
                                </span>
                            </div>
                            {/* Row 2 — plain-language helper explaining what
                                the doctor's choice actually means. Rendered
                                in muted text so it reads as supporting copy,
                                not headline. */}
                            {c.helper && (
                                <p className="mt-0.5 text-[12px] text-muted-foreground leading-snug">
                                    {c.helper}
                                </p>
                            )}
                        </div>

                        {/* Right: inline Change affordance. */}
                        {c.onChange && (
                            <button
                                type="button"
                                onClick={c.onChange}
                                className="shrink-0 self-start mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-primary underline-offset-2 hover:underline transition-colors"
                                data-testid={`step1-answered-chip-${c.key}-change`}
                                title={`Change ${c.label.toLowerCase()}`}
                            >
                                Change
                            </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
}


/* Step1AnsweredStrip — one-line horizontal recap of decisions the
   doctor has already made. Renders nothing until at least one chip
   is present so brand-new Step-1 sessions stay clean. Each chip
   surfaces:
     · Numbered pill in the section's accent tone (01 · 02 · …).
     · Section title (caps, dim) + value (bold, foreground).
     · Inline "Change" button that hands back to the parent, which
       resets the field so the section pops back into the active
       slot below.
*/
export function Step1AnsweredStrip({ chips = [] }) {
    if (!chips.length) return null;
    const toneClasses = {
        primary:  { chip: "bg-primary/10 text-primary ring-primary/30",           text: "text-primary" },
        emerald:  { chip: "bg-emerald-100 text-emerald-700 ring-emerald-300/50",  text: "text-emerald-700" },
        rose:     { chip: "bg-rose-100 text-rose-700 ring-rose-300/50",           text: "text-rose-700" },
        amber:    { chip: "bg-amber-100 text-amber-800 ring-amber-300/50",        text: "text-amber-800" },
    };
    return (
        <div
            className="rounded-2xl border border-border/80 bg-card px-3 py-2.5 flex items-center gap-2 flex-wrap sm:flex-nowrap sm:overflow-x-auto"
            data-testid="step1-answered-strip"
        >
            <span
                className="text-[9px] font-bold tracking-[0.18em] uppercase text-muted-foreground/70 shrink-0"
                aria-hidden
            >
                Your picks
            </span>
            <span className="hidden sm:block h-4 w-px bg-border shrink-0" aria-hidden />
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap min-w-0">
                {chips.map((c, i) => {
                    const t = toneClasses[c.tone] || toneClasses.primary;
                    return (
                        <div
                            key={c.key}
                            className="inline-flex items-center gap-1.5 min-w-0"
                            data-testid={`step1-answered-chip-${c.key}`}
                        >
                            <span
                                className={`shrink-0 w-6 h-6 rounded-md grid place-items-center text-[10px] font-display font-bold tabular-nums ring-1 ${t.chip}`}
                                aria-hidden
                            >
                                {c.num}
                            </span>
                            <span className="text-[9.5px] font-bold tracking-[0.14em] uppercase text-muted-foreground/80 shrink-0">
                                {c.label}
                            </span>
                            <span className={`text-[11.5px] font-semibold truncate max-w-[180px] ${t.text}`} title={c.value}>
                                {c.value}
                            </span>
                            {c.onChange && (
                                <button
                                    type="button"
                                    onClick={c.onChange}
                                    className="text-[10px] font-semibold text-muted-foreground hover:text-primary transition-colors underline-offset-2 hover:underline shrink-0"
                                    data-testid={`step1-answered-chip-${c.key}-change`}
                                    title={`Change ${c.label.toLowerCase()}`}
                                >
                                    Change
                                </button>
                            )}
                            {i < chips.length - 1 && (
                                <span className="text-muted-foreground/30 shrink-0 hidden sm:inline" aria-hidden>·</span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


/* Step1ActiveWrap — the "big card with a subtle teal outline" wrapper
   that holds the currently-active decision. Adds an ambient teal ring
   (matches the CommandHeaderShell grammar) and a 200ms fade+slide-in
   on mount so each new section feels earned (iter101.65). React
   remounts this component via keyed conditional render whenever
   `active` changes, so the animation fires exactly once per section
   transition — no jitter on re-renders inside the same section.
   iter101.67 — Removed the "NOW · Pick your …" eyebrow caption per
   user direction. The teal outline + progressive-disclosure flow are
   themselves the "this is next" signal; the extra pill was noise. */
export function Step1ActiveWrap({ children, testId }) {
    return (
        <div
            className="relative rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/[0.045] via-white to-primary/[0.02] p-5 shadow-[0_1px_0_rgba(15,23,42,0.02),_0_16px_36px_-24px_rgba(20,184,166,0.35)] ring-1 ring-primary/10 animate-in fade-in slide-in-from-top-2 duration-200 ease-out"
            data-testid={testId || "step1-active-wrap"}
        >
            {children}
        </div>
    );
}


/* ═══════════════ review-helpers ═══════════════ */
export function ReviewBlock({ title, children }) {
    return (
        <div className="border-l-2 border-primary/40 pl-4 py-1">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary mb-2">{title}</div>
            <div className="space-y-1.5">{children}</div>
        </div>
    );
}


export function Row({ k, v, mono }) {
    return (
        <div className="flex items-start gap-3 text-[12.5px] leading-relaxed">
            <div className="text-muted-foreground w-32 shrink-0 font-medium">{k}</div>
            <div className={`flex-1 text-foreground/90 ${mono ? "tabular-nums font-semibold" : ""}`}>{v}</div>
        </div>
    );
}


export function SelectField({ label, value, onChange, options, prefix = "", testid }) {
    return (
        <div>
            <Label className="text-xs font-semibold">{label}</Label>
            <select
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-10 mt-1 text-sm rounded-lg border border-border bg-background px-2"
                data-testid={testid}
            >
                <option value="">Select…</option>
                {options.map((opt) => (
                    <option key={opt} value={opt}>{prefix}{opt.replace(/_/g, " ")}</option>
                ))}
            </select>
        </div>
    );
}


/* ─────────── Planning mode picker (Phase 1) ─────────── */
/* ScanSourcePicker — iter97.83
 *
 * Two-card picker presented in Wizard Step 1 right after the
 * Submission Mode (CoE vs Self-plan). Splits the rest of Step 1 down
 * one of two paths:
 *
 *   "upload"      — Doctor will capture scans + photos themselves.
 *                   Reveals Tier / Priority / IO-scanner-toggle /
 *                   Pricing & Advance sections (legacy flow). If the
 *                   doctor has a declared in-house scanner on their
 *                   profile, the card subtitle name-checks the model
 *                   so the UI feels personalised.
 *   "io_service"  — Doctor will book Invisique's IO Scanning Service.
 *                   Reveals a single "Book Scan Visit" CTA which opens
 *                   the wizard quick-create modal. On confirm the
 *                   backend mints a stub case in `scan_visit_scheduled`
 *                   AND a visit row, and we navigate the doctor to the
 *                   patient profile.
 *
 * Both options are shown REGARDLESS of whether the doctor has
 * subscribed to the IO Scanning Service on their profile — clinics
 * with broken scanners or staff out on leave routinely outsource a
 * single case even if they own a scanner.
 */

/* iter98.99q — Shared color helper used by Step 1 cards. Maps the
 * admin's badge_color / accent key into a Tailwind chip class.
 * Keep this aligned with /app/frontend/src/pages/admin/Pricing.jsx
 * ACCENT_OPTIONS. */
const CARD_BADGE_CLASSES = {
    primary: "bg-primary text-primary-foreground",
    slate:   "bg-slate-700 text-white",
    emerald: "bg-emerald-600 text-white",
    amber:   "bg-amber-500 text-white",
    violet:  "bg-violet-600 text-white",
    rose:    "bg-rose-600 text-white",
};
/* iter101.91 — Shared money formatter. Was module-scoped in the parent
   wizard file; extracted so blocks/pickers can reuse it without redefining. */
export const fmtINR = (n) => {
    if (n == null || Number.isNaN(Number(n))) return "\u2014";
    return `\u20B9${Number(n).toLocaleString("en-IN")}`;
};


export function cardBadgeClass(color, fallback = "primary") {
    return CARD_BADGE_CLASSES[color] || CARD_BADGE_CLASSES[fallback] || CARD_BADGE_CLASSES.primary;
}

/* iter98.99q — Phosphor icon resolver for admin-configured icon keys
 * on the Scan Service hero (promises + features). The admin types a
 * Phosphor icon key (e.g. "Clock", "Camera") into Pricing → Wizard
 * Cards; the wizard resolves it here. Unknown keys fall back to
 * Sparkle so the row still renders cleanly. */
const HERO_ICON_MAP = {
    Clock, Camera, Cloud, CloudArrowUp, ShieldCheck, Headset, Truck,
    Receipt, Sparkle, Crown, Star, Tooth, Cube, Lightning, Info,
    HouseLine, HeartStraight, Stethoscope, CheckCircle,
};
export function resolveHeroIcon(key) {
    return HERO_ICON_MAP[key] || Sparkle;
}


/* iter98.99r — Shared "Selected" confirmation pill used on every
 * wizard card (Scan source, Submission mode, Manufacturing speed,
 * Treatment tier, Compliance). Renders as a small uppercase chip
 * with a leading Sparkle glyph + "SELECTED" text, tinted by the
 * card's accent so the visual identity carries through. Inline,
 * sits beside the card title — same grammar as the existing
 * Compliance card so doctors get one consistent "you picked this"
 * confirmation across the whole flow.
 */
const SELECTED_PILL_CLASSES = {
    primary: "bg-gradient-to-r from-primary to-primary text-primary-foreground",
    emerald: "bg-gradient-to-r from-emerald-600 to-emerald-700 text-white",
    slate:   "bg-gradient-to-r from-slate-600 to-slate-700 text-white",
    amber:   "bg-gradient-to-r from-amber-600 to-amber-700 text-white",
    violet:  "bg-gradient-to-r from-violet-600 to-indigo-700 text-white",
    rose:    "bg-gradient-to-r from-rose-600 to-rose-700 text-white",
};
export function SelectedPill({ accent = "primary", testid = "selected-pill" }) {
    const cls = SELECTED_PILL_CLASSES[accent] || SELECTED_PILL_CLASSES.primary;
    return (
        <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold tracking-wider uppercase rounded shadow-sm ${cls}`}
            data-testid={testid}
        >
            {/* iter100.113s — Softer, more confident checkmark grammar.
                Was Sparkle (reads as "+") which felt aggressive; Check
                reads as "confirmed" instead. */}
            <Check size={9} weight="bold" />Selected
        </span>
    );
}


/* ═══════════════ step1-recap ═══════════════ */
export function Step1RecapBanner({
    scanSource,
    planningMode,
    coeSupportCompliance,
    priorityLabel,
    priorityCode,
    isChildCase,
    onEdit,
}) {
    // If absolutely nothing was set yet, suppress the banner — Next is
    // gated upstream so this branch is rare, but bullet-proofs against
    // weird draft-load races.
    if (!scanSource && !planningMode && coeSupportCompliance == null) return null;

    const scanLabel = scanSource === "upload"
        ? "I'll upload scans"
        : scanSource === "io_service"
            ? "IO Scans by Invisique™"
            : "Not picked";
    const modeLabel = planningMode === "coe"
        ? "Invisique™ CoE planning"
        : planningMode === "doctor"
            ? "Self-Plan"
            : "Not picked";
    const complianceLabel = coeSupportCompliance === true
        ? "Yes · CoE Support engaged"
        : coeSupportCompliance === false
            ? "No · self-managed"
            : "Not picked";
    // Section 04 (Mfg speed) only renders for non-child cases with
    // self-uploaded scans. IO Service path picks the speed at scan-visit
    // booking time. Mirror that gating here.
    const showSpeed = !isChildCase && scanSource === "upload";
    const speedLabel = (priorityCode || "").trim()
        ? (priorityLabel || "Standard")
        : "Not picked";

    return (
        <div
            className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/[0.04] via-white to-primary/[0.02] p-4 shadow-sm mb-4"
            data-testid="step1-recap-banner"
        >
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                    <span className="shrink-0 w-7 h-7 rounded-lg bg-primary text-primary-foreground grid place-items-center shadow-sm">
                        <CheckCircle size={15} weight="fill" />
                    </span>
                    <div className="min-w-0">
                        <div className="font-semibold text-[14px] leading-tight text-foreground">
                            Your Step 1 setup
                        </div>
                        <div className="text-[11px] text-muted-foreground leading-snug">
                            Quick recap of the choices you committed to.
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onEdit}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11.5px] font-semibold text-primary hover:bg-primary/[0.08] rounded-md transition-colors border border-primary/20 hover:border-primary/40"
                    data-testid="step1-recap-edit-btn"
                >
                    <Pencil size={12} weight="bold" />
                    Edit setup
                </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                <RecapChip num="01" accent="primary" label="Scan source"      value={scanLabel} />
                <RecapChip num="02" accent="emerald" label="Submission mode"  value={modeLabel} />
                <RecapChip num="03" accent="rose"    label="Compliance"       value={complianceLabel} />
                {showSpeed && (
                    <RecapChip num="04" accent="amber" label="Mfg speed"      value={speedLabel} />
                )}
            </div>
        </div>
    );
}

/* Single Step-1 recap chip — same colour family as the corresponding
 * Step 1 section header, so the visual identity carries through. */
export function RecapChip({ num, accent, label, value }) {
    const palette = {
        primary: { num: "bg-primary/10 text-primary",            border: "border-primary/20" },
        emerald: { num: "bg-emerald-100 text-emerald-700",       border: "border-emerald-200/70" },
        rose:    { num: "bg-rose-100 text-rose-700",             border: "border-rose-200/70" },
        amber:   { num: "bg-amber-100 text-amber-800",           border: "border-amber-200/70" },
    };
    const p = palette[accent] || palette.primary;
    return (
        <div className={`rounded-lg border ${p.border} bg-white px-2.5 py-2 flex items-center gap-2`}>
            <span className={`shrink-0 w-7 h-7 rounded-md grid place-items-center text-[10px] font-bold tabular-nums ${p.num}`}>
                {num}
            </span>
            <div className="min-w-0 flex-1">
                <div className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-muted-foreground leading-none">{label}</div>
                <div className="text-[12.5px] font-semibold text-foreground truncate mt-0.5 leading-snug" title={value}>
                    {value}
                </div>
            </div>
        </div>
    );
}
